import axios from 'axios';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { API_CONFIG } from '@/config/api';

/**
 * Get the current Cognito ID token via Amplify's session manager.
 * Amplify handles token caching, refresh, and storage internally —
 * no manual localStorage needed.
 *
 * @returns {Promise<string|null>} ID token string, or null if not authenticated
 */
export async function getIdToken() {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

// Create axios instance for platform API
// Auth is via Bearer token in Authorization header (not cookies),
// so withCredentials is NOT set — this allows CORS with specific origins.
const platformApi = axios.create({
  baseURL: API_CONFIG.platformUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token (async — fetches fresh token from Amplify)
platformApi.interceptors.request.use(
  async (config) => {
    const token = await getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
platformApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Sign out via Amplify (clears its internal token store).
      // Only redirect if we're not already on auth pages (prevents infinite loops).
      const path = window.location.pathname;
      if (!path.startsWith('/login') && !path.startsWith('/auth')) {
        try {
          await signOut();
        } catch {
          // Ignore — we're redirecting anyway
        }
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authApi = {
  exchangeCode: (code) =>
    platformApi.post('/auth/token', { code, grant_type: 'authorization_code' }),

  refreshToken: (refreshToken) =>
    platformApi.post('/auth/token', { refresh_token: refreshToken, grant_type: 'refresh_token' }),

  logout: () =>
    platformApi.post('/auth/logout'),

  getUser: () =>
    platformApi.get('/auth/me'),

  acceptTerms: (versions = {}) =>
    platformApi.post('/auth/accept-terms', versions),

  getTermsStatus: () =>
    platformApi.get('/auth/terms-status'),
};

// Apps endpoints
export const appsApi = {
  list: () =>
    platformApi.get('/apps'),

  get: (appId) =>
    platformApi.get(`/apps/${appId}`),

  create: (data) =>
    platformApi.post('/apps', data),

  update: (appId, data) =>
    platformApi.put(`/apps/${appId}`, data),

  delete: (appId) =>
    platformApi.delete(`/apps/${appId}`),
};

// API Keys endpoints
export const apiKeysApi = {
  list: (appId) =>
    platformApi.get(`/apps/${appId}/keys`),

  create: (appId, data) =>
    platformApi.post(`/apps/${appId}/keys`, data),

  revoke: (appId, keyId) =>
    platformApi.delete(`/apps/${appId}/keys/${keyId}`),
};

// Billing endpoints
export const billingApi = {
  getUsage: (appId, period = 'ytd', source = null) =>
    platformApi.get(`/billing/usage/${appId}`, {
      params: { period, tz_offset: new Date().getTimezoneOffset(), ...(source && { source }) },
    }),

  getUsageHistory: (appId, period = 'month', source = null) =>
    platformApi.get(`/billing/usage/${appId}/history`, {
      params: { period, tz_offset: new Date().getTimezoneOffset(), ...(source && { source }) },
    }),

  getPlans: () =>
    platformApi.get('/billing/plans'),

  createCheckout: (appId, planTier, successUrl, cancelUrl) =>
    platformApi.post('/billing/checkout', null, {
      params: { app_id: appId, plan_tier: planTier, success_url: successUrl, cancel_url: cancelUrl },
    }),

  createBillingPortal: (appId, returnUrl) =>
    platformApi.post('/billing/portal', null, {
      params: { app_id: appId, return_url: returnUrl },
    }),
};

// Mind / Consciousness endpoints (data plane)
export const mindApi = {
  getPresence: (appId) =>
    platformApi.get(`/v1/apps/${appId}/mind/presence`),

  getThoughts: (appId, params = {}) =>
    platformApi.get(`/v1/apps/${appId}/mind/thoughts`, { params }),

  query: (appId, body) =>
    platformApi.post(`/v1/apps/${appId}/query`, body),

  // Curiosity (root_id required)
  getCuriosity: (appId, params = {}) =>
    platformApi.get(`/v1/apps/${appId}/curiosity`, { params }),

  triggerCuriosity: (appId, rootId) =>
    platformApi.post(`/v1/apps/${appId}/curiosity`, { root_id: rootId }),

  dismissCuriosity: (appId, packetId) =>
    platformApi.put(`/v1/apps/${appId}/curiosity/${packetId}/dismiss`),

  // Root nodes
  getRoots: (appId) =>
    platformApi.get(`/v1/apps/${appId}/roots`),
};

// Intelligence Registry endpoints
export const intelligenceApi = {
  getLayers: (appId) =>
    platformApi.get(`/apps/${appId}/intelligence/layers`),

  getLayer: (appId, layerId) =>
    platformApi.get(`/apps/${appId}/intelligence/layers/${layerId}`),

  updateLayer: (appId, layerId, data) =>
    platformApi.put(`/apps/${appId}/intelligence/layers/${layerId}`, data),

  getDebugSettings: (appId) =>
    platformApi.get(`/apps/${appId}/intelligence/debug-settings`),

  updateDebugSettings: (appId, data) =>
    platformApi.put(`/apps/${appId}/intelligence/debug-settings`, data),

  getDebugLogs: (appId, params = {}) =>
    platformApi.get(`/apps/${appId}/intelligence/debug-logs`, { params }),

  getDebugLog: (appId, logId) =>
    platformApi.get(`/apps/${appId}/intelligence/debug-logs/${logId}`),

  purgeDebugLogs: (appId) =>
    platformApi.delete(`/apps/${appId}/intelligence/debug-logs`),
};

// Schema endpoints
export const schemaApi = {
  generate: (data) =>
    platformApi.post('/schema/generate', data),

  get: (appId) =>
    platformApi.get(`/apps/${appId}/schema`),

  update: (appId, data) =>
    platformApi.put(`/apps/${appId}/schema`, data),

  generateCuriosityPrompts: (appId) =>
    platformApi.post(`/apps/${appId}/schema/generate-curiosity-prompts`),
};

// Graph Explorer endpoints (data plane)
export const graphApi = {
  getOverview: (appId, params = {}) =>
    platformApi.get(`/v1/apps/${appId}/graph/overview`, { params }),

  getNeighbors: (appId, nodeId, params = {}) =>
    platformApi.get(`/v1/apps/${appId}/graph/neighbors/${encodeURIComponent(nodeId)}`, { params }),

  search: (appId, params = {}) =>
    platformApi.get(`/v1/apps/${appId}/graph/search`, { params }),

  getNode: (appId, nodeId) =>
    platformApi.get(`/v1/apps/${appId}/graph/node/${encodeURIComponent(nodeId)}`),
};

// Entity API (data plane)
const euc = encodeURIComponent;
export const entityApi = {
  list: (appId, params = {}) =>
    platformApi.get(`/v1/apps/${appId}/entities`, { params }),

  get: (appId, entityId) =>
    platformApi.get(`/v1/apps/${appId}/entities/${euc(entityId)}`),

  create: (appId, data) =>
    platformApi.post(`/v1/apps/${appId}/entities`, data),

  update: (appId, entityId, data) =>
    platformApi.put(`/v1/apps/${appId}/entities/${euc(entityId)}`, data),

  delete: (appId, entityId) =>
    platformApi.delete(`/v1/apps/${appId}/entities/${euc(entityId)}`),

  createEdge: (appId, data) =>
    platformApi.post(`/v1/apps/${appId}/entities/edges`, data),

  deleteEdge: (appId, data) =>
    platformApi.delete(`/v1/apps/${appId}/entities/edges`, { data }),

  getEdges: (appId, entityId, params = {}) =>
    platformApi.get(`/v1/apps/${appId}/entities/${euc(entityId)}/edges`, { params }),

  batch: (appId, data) =>
    platformApi.post(`/v1/apps/${appId}/entities/batch`, data),
};

// Recall (Semantic Memory) endpoints (data plane)
export const recallApi = {
  query: (appId, body) =>
    platformApi.post(`/v1/apps/${appId}/recall`, body),
};

// WebSocket auth endpoints
export const wsApi = {
  /** Get a short-lived single-use ticket for WebSocket authentication */
  getTicket: () =>
    platformApi.post('/auth/ws-ticket').then((res) => res.data?.data?.ticketId),
};

// Platform Service Catalog endpoints
export const serviceCatalogApi = {
  list: () =>
    platformApi.get('/service-catalog'),

  get: (serviceId) =>
    platformApi.get(`/service-catalog/${encodeURIComponent(serviceId)}`),

  update: (serviceId, data) =>
    platformApi.put(`/service-catalog/${encodeURIComponent(serviceId)}`, data),

  seed: () =>
    platformApi.post('/service-catalog/seed'),

  reseed: () =>
    platformApi.post('/service-catalog/reseed'),

  test: (serviceId, data) =>
    platformApi.post(`/service-catalog/${encodeURIComponent(serviceId)}/test`, data),

  create: (data) =>
    platformApi.post('/service-catalog', data),

  delete: (serviceId) =>
    platformApi.delete(`/service-catalog/${encodeURIComponent(serviceId)}`),

  models: () =>
    platformApi.get('/service-catalog/models'),
};

// Public API client — no auth, no interceptors
const publicApi = axios.create({
  baseURL: API_CONFIG.platformUrl,
  headers: { 'Content-Type': 'application/json' },
});

// Documentation endpoints
export const docsApi = {
  // Public endpoints (no auth required)
  list: () =>
    publicApi.get('/public/docs'),

  get: (slug) =>
    publicApi.get(`/public/docs/${encodeURIComponent(slug)}`),

  update: (slug, data) =>
    platformApi.put(`/docs/${encodeURIComponent(slug)}`, data),

  create: (data) =>
    platformApi.post('/docs', data),

  delete: (slug) =>
    platformApi.delete(`/docs/${encodeURIComponent(slug)}`),

  seed: () =>
    platformApi.post('/docs/seed'),

  reseed: () =>
    platformApi.post('/docs/reseed'),
};

// User settings endpoints
export const settingsApi = {
  getProfile: () =>
    platformApi.get('/users/profile'),

  updateProfile: (data) =>
    platformApi.put('/users/profile', data),

  getNotificationPreferences: () =>
    platformApi.get('/users/notifications'),

  updateNotificationPreferences: (data) =>
    platformApi.put('/users/notifications', data),
};

export default platformApi;
